/**
 * Native email/password auth client for the `/api/v1/auth/*` surface.
 *
 * These are the pre-session flows (register, verify, login, forgot/reset
 * password, resend-verification) a prospect drives before they hold a token,
 * so every call is anonymous and same-origin via the shell's API base URL.
 *
 * `apiFetchJson` only surfaces a `message` field on error bodies, but the
 * gateway returns RFC-7807 ProblemDetails (`title` / `detail`) for these
 * endpoints. This module does its own fetch so a failed login/verify shows the
 * server's `detail` (e.g. "Email not verified") rather than a bare status text.
 */

import { buildApiUrl } from '@tyndo/hooks';

/** Key `apiFetch.ts` `readToken()` reads — the issued session JWT lives here. */
export const AUTH_TOKEN_STORAGE_KEY = 'auth_token';

/** Issued session token + its absolute expiry (camelCased gateway response). */
export interface SessionResponse {
  token: string;
  expiresAt: string;
}

/** Registration result — no session token until the email is verified. */
export interface RegisterResponse {
  email: string;
  requiresVerification: boolean;
}

/** Generic acknowledgement for the enumeration-safe forgot/reset flows. */
export interface AcknowledgedResponse {
  message: string;
}

/** Error carrying the gateway's resolved status code for caller branching. */
export class AuthApiError extends Error {
  readonly statusCode: number;

  constructor(message: string, statusCode: number) {
    super(message);
    this.name = 'AuthApiError';
    this.statusCode = statusCode;
  }
}

async function postAuth<T>(path: string, body: unknown): Promise<T> {
  // audit-allow: direct-fetch -- auth bootstrap transport: login/refresh run
  // before a token (and therefore the shared authenticated API client) exists,
  // so this must use raw fetch.
  const res = await fetch(buildApiUrl(`/api/v1/auth${path}`), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    // Prefer ProblemDetails `detail`, then `title`/`message`, then status text.
    let message = res.statusText || 'Request failed';
    try {
      const data = (await res.json()) as {
        detail?: string;
        title?: string;
        message?: string;
      };
      message = data?.detail || data?.message || data?.title || message;
    } catch {
      /* non-JSON body — keep status text */
    }
    throw new AuthApiError(message, res.status);
  }

  if (res.status === 204) return undefined as T;
  const ct = res.headers.get('content-type') ?? '';
  if (ct.includes('application/json')) {
    return (await res.json()) as T;
  }
  return undefined as T;
}

export function register(input: {
  companyName: string;
  email: string;
  password: string;
}): Promise<RegisterResponse> {
  return postAuth<RegisterResponse>('/register', {
    companyName: input.companyName,
    email: input.email,
    password: input.password,
  });
}

export function login(input: {
  email: string;
  password: string;
}): Promise<SessionResponse> {
  return postAuth<SessionResponse>('/login', {
    email: input.email,
    password: input.password,
  });
}

export function verify(token: string): Promise<SessionResponse> {
  return postAuth<SessionResponse>('/verify', { token });
}

export function forgotPassword(email: string): Promise<AcknowledgedResponse> {
  return postAuth<AcknowledgedResponse>('/forgot-password', { email });
}

export function resetPassword(input: {
  token: string;
  newPassword: string;
}): Promise<AcknowledgedResponse> {
  return postAuth<AcknowledgedResponse>('/reset-password', {
    token: input.token,
    newPassword: input.newPassword,
  });
}

export function resendVerification(email: string): Promise<AcknowledgedResponse> {
  return postAuth<AcknowledgedResponse>('/resend-verification', { email });
}

/**
 * Persist an issued session token under the same key `apiFetch.ts`'s
 * `readToken()` reads, so subsequent authenticated API calls pick it up.
 */
export function storeSessionToken(token: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(AUTH_TOKEN_STORAGE_KEY, token);
  } catch {
    /* storage unavailable (private mode) — surfaced as a failed next call */
  }
}
